import { Button, Flex, FlexProps, MantineSize, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { Fragment, ReactNode } from 'react';
import { Link } from 'react-router';

export type BreadcrumbItem = { label: string | null; href: string; highlight?: boolean };

type Props = FlexProps & {
  items: BreadcrumbItem[];
  // Whether the last item is the page being shown, which reads in bold. A trail that leads
  // back from a page with its own title stops short of it, so nothing is bold.
  endsAtCurrentPage?: boolean;
  /** Font size of every crumb. */
  size?: MantineSize;
  /** Colour of the crumbs that are neither the current page nor highlighted. */
  color?: string;
  /** Drops the crumbs' button padding so the trail is only as tall as its text. */
  compact?: boolean;
  children?: ReactNode;
};

/** A row of links back up the app, separated by chevrons. */
export function BreadcrumbTrail({
  items,
  endsAtCurrentPage = true,
  size = 'md',
  color = 'gray.9',
  compact = false,
  children,
  ...flexProps
}: Props) {
  return (
    <Flex align="center" wrap="wrap" gap="xs" style={{ minWidth: 0 }} {...flexProps}>
      {items.map((item, i) => {
        const isCurrent = endsAtCurrentPage && i === items.length - 1;
        const textProps = isCurrent ? { c: 'gray.10', fw: 700 } : { c: color, fw: 500 };

        if (item.highlight) {
          textProps.c = 'brand.6';
        }

        return (
          <Fragment key={item.href}>
            <Button
              component={Link}
              variant="text"
              to={item.href}
              h={compact ? 'auto' : undefined}
              p={compact ? 0 : undefined}
            >
              <Text size={size} {...textProps}>
                {item.label}
              </Text>
            </Button>
            {i < items.length - 1 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--mantine-color-gray-7)',
                }}
              >
                <IconChevronRight size={16} />
              </span>
            )}
          </Fragment>
        );
      })}
      {children}
    </Flex>
  );
}
